import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Portal from '../components/Portal';
import { useAuth } from '../context/AuthContext';
import { useNav } from '../context/NavContext';
import { toneShiftApi } from '../services/toneShiftApi';
import { useTouchHandler } from '../hooks/useTouchHandler';
import { useViewportHeights } from '../hooks/useViewportHeights';
import { activeModifierLevels, buildPlaceholder, notifyError } from '../utils/selectionUtils';
import { buildToneShiftPrompt } from '../utils/promptBuilder';
import { analyzeText } from '../utils/analyzeText';
import { getModifierLevelName as getModifierLevelNameFromMapping, getModifierLevelDisplayName } from '../constants/toneShiftMappings';
import { toast } from 'react-toastify';
import { useModelSelector } from '../hooks/useModelSelector';


import Selector from '../components/Selector';
import { 
  Plus, 
  ChevronDown, 
  Edit3, 
  X, 
  Copy, 
  Eye, 
  Info, 
  BarChart3, 
  Clock, 
  Wand2,
  Save,
  Sparkles,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Chips from '../components/Chips';
import MessageInfoModal from '../components/MessageInfoModal';
import HistoryModal from '../components/modals/HistoryModal';
import AnalysisEditModal from '../components/modals/AnalysisEditModal';
import AddModal from '../components/modals/AddModal';
import PromptPreviewModal from '../components/modals/PromptPreviewModal';
import EditModal from '../components/modals/EditModal';

import '../styles/ToneShiftPage.css';












const ToneShiftPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  // Refs for dynamic height measurement
  const headerRef = useRef(null);
  const inputRef = useRef(null);
  const messagesAreaRef = useRef(null);
  
  const [inputText, setInputText] = useState("");
  
  const [selectedVoice, setSelectedVoice] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [modifierLevels, setModifierLevels] = useState({});
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  
  // Model selection state
  const { availableModels, selectedModel, select: setSelectedModel, loading: modelsLoading, error: modelsError } = useModelSelector();
  
  // Empty lists by default
  const [voices, setVoices] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formats, setFormats] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  
  const [messages, setMessages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [messageAnalyses, setMessageAnalyses] = useState({});
  
  // Modifiers dropdown state - REMOVED: search functionality removed
  // const modifiersBtnRef = useRef(null);
  // const [modsOpen, setModsOpen] = useState(false);
  // const [modsPos, setModsPos] = useState({ top: 0, left: 0, width: 320 });

  // For the modifiers bottom sheet drag - REMOVED: no longer needed
  // const sheetRef = useRef(null);
  // const [dragY, setDragY] = useState(0);
  // const [isDragging, setIsDragging] = useState(false);
  // const startYRef = useRef(0);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // REMOVED: Touch handlers no longer needed since modifiers use Selector component
  // const onSheetTouchStart = (e) => { ... };
  // const onSheetTouchMove = (e) => { ... };
  // const onSheetTouchEnd = () => { ... };

  // Analysis prompt state
  const [analysisPrompt, setAnalysisPrompt] = useState('Analyze the text to see what tones come across and what the key message is');
  const [showAnalysisEditModal, setShowAnalysisEditModal] = useState(false);

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Conversation history
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    prompt: ''
  });

  // Prompt viewer modal state

  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [selectedStructuredJson, setSelectedStructuredJson] = useState(null);
  
  // Mobile navigation state
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Navigation items for mobile menu
  const navigationItems = [
    {
      id: 'library',
      title: 'Library',
      path: '/prompt-manager',
      icon: '/images/library.png',
      iconGif: '/images/library.gif'
    },
    {
      id: 'tone-shift',
      title: 'Tone Shift',
      path: '/tone-shift',
      icon: '/images/toneshift.png',
      iconGif: '/images/toneshift.gif'
    },
    {
      id: 'chat',
      title: 'Chat',
      path: '/chat',
      icon: '/images/chat.png',
      iconGif: '/images/chat.gif'
    },
    {
      id: 'workflows',
      title: 'Workflows',
      path: '/workflows',
      icon: '/images/workflows.png',
      iconGif: '/images/workflows.gif'
    },
    {
      id: 'prototyper',
      title: 'Prototyper',
      path: '/prototyper',
      icon: '/images/prototype.png',
      iconGif: '/images/prototype.gif'
    }
  ];
  


  // Modifier edit modal states
  const [showModifierEditModal, setShowModifierEditModal] = useState(false);
  const [editingModifier, setEditingModifier] = useState(null);
  const [modifierEditForm, setModifierEditForm] = useState({
    name: '',
    description: '',
    left: '',
    right: '',
    prompts: {
      '-3': '',
      '-2': '',
      '-1': '',
      '0': '',
      '1': '',
      '2': '',
      '3': ''
    },
    levelLabels: {
      '-3': '',
      '-2': '',
      '-1': '',
      '0': 'Balanced',
      '1': '',
      '2': '',
      '3': ''
    }
  });

  // Add new item states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    description: '',
    prompt: '',
    type: ''
  });

  // Add modifier states
  const [showAddModifierModal, setShowAddModifierModal] = useState(false);
  const [addModifierForm, setAddModifierForm] = useState({
    name: '',
    description: '',
    left: '',
    right: '',
    prompts: {
      '-3': '',
      '-2': '',
      '-1': '',
      '0': '',
      '1': '',
      '2': '',
      '3': ''
    },
    levelLabels: {
      '-3': '',
      '-2': '',
      '-1': '',
      '0': 'Balanced',
      '1': '',
      '2': '',
      '3': ''
    }
  });


  const [showEditSlideModal, setShowEditSlideModal] = useState(false);
  const [editingSlideItem, setEditingSlideItem] = useState(null);
  

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Analysis slide modal states
  const [showAnalysisSlideModal, setShowAnalysisSlideModal] = useState(false);
  const [selectedAnalysisMessage, setSelectedAnalysisMessage] = useState(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);

  const { isNavCollapsed } = useNav();



  // Message information modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageType, setSelectedMessageType] = useState(null);


  // Prompt preview state




  // Handle modifier save
  const handleAddModifierSave = async () => {
    try {
      const data = {
        name: addModifierForm.name,
        description: addModifierForm.description,
        left: addModifierForm.left,
        right: addModifierForm.right,
        prompts: addModifierForm.prompts,
        level_labels: addModifierForm.levelLabels
      };

      await toneShiftApi.createModifier(data);
      
      toast.success('Modifier created successfully');
      setShowAddModifierModal(false);
      setAddModifierForm({
        name: '',
        description: '',
        left: '',
        right: '',
        prompts: {
          '-3': '',
          '-2': '',
          '-1': '',
          '0': '',
          '1': '',
          '2': '',
          '3': ''
        },
        levelLabels: {
          '-3': '',
          '-2': '',
          '-1': '',
          '0': 'Balanced',
          '1': '',
          '2': '',
          '3': ''
        }
      });
      
      // Refresh the modifiers list
      fetchToneShiftItems();
    } catch (error) {
      notifyError('creating modifier', error, toast);
    }
  };

  // Model selection is now handled by useModelSelector hook

  const fetchToneShiftItems = async () => {
    try {
        // Fetch all parameter types in parallel with individual error handling
  const fetchWithErrorHandling = async (fetchFunction, name) => {
    try {
      const data = await fetchFunction();
      return data;
    } catch (error) {
      notifyError(`fetching ${name}`, error, toast);
      return [];
    }
  };
      
          const [voicesData, rolesData, formatsData, audiencesData, modifiersData] = await Promise.all([
      fetchWithErrorHandling(() => toneShiftApi.getVoices(), 'voices'),
      fetchWithErrorHandling(() => toneShiftApi.getRoles(), 'roles'),
      fetchWithErrorHandling(() => toneShiftApi.getFormats(), 'formats'),
      fetchWithErrorHandling(() => toneShiftApi.getAudiences(), 'audiences'),
      fetchWithErrorHandling(() => toneShiftApi.getModifiers(), 'modifiers')
    ]);
      
      // Data is already transformed by the API service
      const voicesList = voicesData;
      const rolesList = rolesData;
      const formatsList = formatsData;
      const audiencesList = audiencesData;
      const modifiersList = modifiersData;
      
      
      
      // Check if any items are missing names
      const checkMissingNames = (items, type) => {
        const missing = items.filter(item => !item.name);
        if (missing.length > 0) {
          console.warn(`Missing names in ${type}:`, missing);
        }
      };
      
      checkMissingNames(voicesList, 'voices');
      checkMissingNames(rolesList, 'roles');
      checkMissingNames(formatsList, 'formats');
      checkMissingNames(audiencesList, 'audiences');
      checkMissingNames(modifiersList, 'modifiers');
      
      setVoices(voicesList);
      setRoles(rolesList);
      setFormats(formatsList);
      setAudiences(audiencesList);
      setModifiers(modifiersList);
      

      
    } catch (error) {
      notifyError('fetching tone shift items', error, toast);
      
      // Fallback to empty arrays or predefined items
      setVoices([]);
      setRoles([]);
      setFormats([]);
      setAudiences([]);
      setModifiers([]);
    }
  };

  // For now, we'll keep analysis prompt as a simple state since it's not critical to the main functionality
  // In a future update, this could be moved to the database as a user preference
  const fetchAnalysisPrompt = async () => {
    // Placeholder for future implementation
    // For now, we use the default analysis prompt
  };

  useEffect(() => {
    // Check if user is authenticated
    // Temporarily disabled for development testing
    /*
    if (!loading && !user) {
      toast.error('Please log in to access tone shift features');
      navigate('/login');
      return;
    }
    */
    
    // if (user) {
    fetchToneShiftItems();
    fetchAnalysisPrompt();
    
    // Handle URL parameters for pre-loaded content
    const urlParams = new URLSearchParams(location.search);
    const preloadedContent = urlParams.get('content');
    const preloadedTitle = urlParams.get('title');
    
    if (preloadedContent) {
      setInputText(preloadedContent);
      // Clear URL parameters after loading to clean up the URL
              navigate('/tone-shift', { replace: true });
      toast.info(`Loaded content: ${preloadedTitle || 'Untitled'}`);
    }
    // }
  }, [user, loading, location.search, navigate]);

  // Use viewport heights hook for iOS viewport fixes
  useViewportHeights(headerRef, inputRef);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    requestAnimationFrame(scrollToBottom);
  }, [messages]);

  // Update selected analysis message when analysis data changes
  useEffect(() => {
    if (selectedAnalysisMessage && selectedAnalysisMessage.id) {
      const updatedAnalysis = messageAnalyses[selectedAnalysisMessage.id];
      
      if (updatedAnalysis && !selectedAnalysisMessage.analysis) {
        setSelectedAnalysisMessage(prev => ({
          ...prev,
          analysis: updatedAnalysis
        }));
        setIsAnalysisLoading(false);
      }
    }
  }, [messageAnalyses, selectedAnalysisMessage]);

  // Handle window resize for mobile orientation changes
  useEffect(() => {
    const handleResize = () => {
      // Re-scroll to bottom when window resizes (orientation change)
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);




  const handleAnalyzeText = (text, messageId) => {
    try {
      const analysis = analyzeText(text, analysisPrompt);
      if (analysis) {
        setMessageAnalyses(prev => ({ ...prev, [messageId]: analysis }));
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, analysis } : msg
        ));
      }
    } catch (error) {
      notifyError('analyzing text', error, toast);
    }
  };

  const handleMessageAnalyze = (messageId) => {
    const message = messages.find(m => m.id === messageId);
    if (message && message.text.trim()) {
      // If message already has analysis, show it in slide modal
      if (message.analysis || messageAnalyses[messageId]) {
        const messageWithAnalysis = {
          ...message,
          analysis: messageAnalyses[messageId] || message.analysis
        };
        setSelectedAnalysisMessage(messageWithAnalysis);
        setShowAnalysisSlideModal(true);
      } else {
        // Show loading state and run analysis
        setIsAnalysisLoading(true);
        setSelectedAnalysisMessage(message);
        setShowAnalysisSlideModal(true);
        
        handleAnalyzeText(message.text, messageId);
        // Get the updated message with analysis
        const updatedMessage = messages.find(m => m.id === messageId);
        if (updatedMessage) {
          // Update the selected message with the analysis data
          const messageWithAnalysis = {
            ...updatedMessage,
            analysis: messageAnalyses[messageId]
          };
          setSelectedAnalysisMessage(messageWithAnalysis);
        }
        setIsAnalysisLoading(false);
      }
    }
  };





  const handleSend = async () => {
    if (!canSend({ showToast: true })) {
      return;
    }

    // Determine if this is the first message or additional instructions
    const isFirstMessage = messages.length === 0;
    const messageType = isFirstMessage ? 'original_message' : 'additional_instructions';

    const userMessage = {
      id: Date.now(),
      type: 'input',
      text: inputText || '(Parameter update only)',
      timestamp: new Date().toLocaleTimeString(),
      voice: selectedVoice,
      role: selectedRole,
      format: selectedFormat,
      modifiers: Object.fromEntries(
        Object.entries(modifierLevels)
          .filter(([_, level]) => parseInt(level) !== 0)
          .map(([key, level]) => [key, parseInt(level)])
      ),
      audience: selectedAudiences
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsGenerating(true);

    try {
      // If this is the first message in a conversation, create a new conversation
      if (!currentConversationId) {
        
        // Create a new conversation with the current parameters
        const conversationData = {
          title: inputText.slice(0, 50) + (inputText.length > 50 ? '...' : ''),
          original_content: inputText,
          voice_ids: selectedVoice ? [selectedVoice] : [],
          role_ids: selectedRole ? [selectedRole] : [],
          format_ids: selectedFormat ? [selectedFormat] : [],
          audience_ids: selectedAudiences,
          modifier_ids: [], // Starter prompts don't have database IDs, so send empty array
          modifier_levels: Object.fromEntries(
            Object.entries(modifierLevels)
              .filter(([_, level]) => parseInt(level) !== 0)
              .map(([key, level]) => [key, parseInt(level)])
          )
        };

        const newConversation = await toneShiftApi.createConversation(conversationData);
        setCurrentConversationId(newConversation.id);

        // Send the initial message
        const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
        
        // Build the complete formatted prompt on the frontend
        const { text: completePrompt } = buildToneShiftPrompt({
          inputText,
          messages,
          voices, roles, formats, audiences, modifiers,
          selectedVoice, selectedRole, selectedFormat, selectedAudiences,
          modifierLevelsMap: activeModifierLevels(modifierLevels),
          selectedModelInfo,
          getModifierLevelName,
          getLevelDisplayName
        });
        
        const messageResult = await toneShiftApi.sendMessage(
          newConversation.id, 
          {
            content: completePrompt,
            message_type: messageType,
            provider: selectedModelInfo ? selectedModelInfo.provider : null,
            model: selectedModelInfo ? selectedModelInfo.model : null
          }
        );
        

        
        if (messageResult.ai_response) {
          
          // Update the user message with metadata
            setMessages(prev => prev.map(msg => 
            msg.id === userMessage.id ? { 
              ...msg, 
              metadata: messageResult.user_message?.metadata || {},
              prompt: messageResult.ai_response.metadata?.actual_prompt_sent
            } : msg
          ));

          const outputMessage = {
            id: Date.now() + 1,
            type: 'output',
            text: messageResult.ai_response.content,
            additionalContext: messageResult.ai_response.metadata?.additional_context || '',
            messageType: 'updated_response',
            timestamp: new Date().toLocaleTimeString(),
            voice: selectedVoice,
            role: selectedRole,
            format: selectedFormat,
            modifiers: Object.fromEntries(
              Object.entries(modifierLevels)
                .filter(([_, level]) => parseInt(level) !== 0)
                .map(([key, level]) => [key, parseInt(level)])
            ),
            audience: selectedAudiences,
            metadata: messageResult.ai_response.metadata || {}
          };
          
          setMessages(prev => [...prev, outputMessage]);
          
          // Save to conversation history
          saveToConversationHistory(userMessage.text, outputMessage.text, outputMessage);
        } else {
          throw new Error(messageResult.error || 'Failed to get AI response');
        }
      } else {
        // Send message to existing conversation
        const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
        
        // Update conversation parameters first
        const conversationParameters = {
          voice_ids: selectedVoice ? [selectedVoice] : [],
          role_ids: selectedRole ? [selectedRole] : [],
          format_ids: selectedFormat ? [selectedFormat] : [],
          audience_ids: selectedAudiences,
          modifier_ids: [], // Starter prompts don't have database IDs, so send empty array
          modifier_levels: Object.fromEntries(
            Object.entries(modifierLevels)
              .filter(([_, level]) => parseInt(level) !== 0)
              .map(([key, level]) => [key, parseInt(level)])
          )
        };
        
        await toneShiftApi.updateParameters(currentConversationId, conversationParameters);
        
        const modelInfo = selectedModelInfo ? { provider: selectedModelInfo.provider, model: selectedModelInfo.model } : null;
        
        // Build the complete formatted prompt on the frontend
        const { text: completePrompt } = buildToneShiftPrompt({
          inputText,
          messages,
          voices, roles, formats, audiences, modifiers,
          selectedVoice, selectedRole, selectedFormat, selectedAudiences,
          modifierLevelsMap: activeModifierLevels(modifierLevels),
          selectedModelInfo,
          getModifierLevelName,
          getLevelDisplayName
        });
        
        const messageResult = await toneShiftApi.sendMessage(
          currentConversationId, 
          {
            content: completePrompt,
            message_type: messageType,
            provider: modelInfo ? modelInfo.provider : null,
            model: modelInfo ? modelInfo.model : null
          }
        );
        

        
        if (messageResult.ai_response) {
          // Update the user message with metadata
            setMessages(prev => prev.map(msg => 
            msg.id === userMessage.id ? { 
              ...msg, 
              metadata: messageResult.user_message?.metadata || {},
              prompt: messageResult.ai_response.metadata?.actual_prompt_sent
            } : msg
          ));

          const outputMessage = {
            id: Date.now() + 1,
            type: 'output',
            text: messageResult.ai_response.content,
            additionalContext: messageResult.ai_response.metadata?.additional_context || '',
            messageType: 'updated_response',
            timestamp: new Date().toLocaleTimeString(),
            voice: selectedVoice,
            role: selectedRole,
            format: selectedFormat,
            modifiers: Object.fromEntries(
              Object.entries(modifierLevels)
                .filter(([_, level]) => parseInt(level) !== 0)
                .map(([key, level]) => [key, parseInt(level)])
            ),
            audience: selectedAudiences,
            metadata: messageResult.ai_response.metadata || {}
          };
          
          setMessages(prev => [...prev, outputMessage]);
          
          // Save to conversation history
          saveToConversationHistory(userMessage.text, outputMessage.text, outputMessage);
        } else {
          throw new Error(messageResult.error || 'Failed to get AI response');
        }
      }
    } catch (error) {
      notifyError('sending message', error, toast);
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        type: 'output',
        text: 'Sorry, I encountered an error processing your message. Please try again.',
        timestamp: new Date().toLocaleTimeString(),
        voice: selectedVoice,
        role: selectedRole,
        format: selectedFormat,
        modifiers: Object.fromEntries(
          Object.entries(modifierLevels)
            .filter(([_, level]) => parseInt(level) !== 0)
            .map(([key, level]) => [key, parseInt(level)])
        ),
        audience: selectedAudiences,
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsGenerating(false);
      
      // Scroll to bottom after message is added
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  const scrollToBottom = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    // ensure layout finished before scrolling
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  };

  const saveToConversationHistory = (input, output, message) => {
    if (!currentConversationId) return; // simple and reliable
    
    const conversation = {
      id: currentConversationId,
      title: input.slice(0, 50) + (input.length > 50 ? '...' : ''),
      timestamp: new Date().toLocaleString(),
      input: input,
      output: output,
      message: message
    };
    
    setConversations(prev => {
      const existingIndex = prev.findIndex(c => c.id === currentConversationId);
      if (existingIndex >= 0) {
        // Update existing conversation
        const updated = [...prev];
        updated[existingIndex] = conversation;
        return updated;
      } else {
        // Add new conversation
        return [conversation, ...prev];
      }
    });
  };

  const startNewConversation = () => {
    setInputText('');
    setMessages([]);
    setSelectedVoice('');
    setSelectedRole('');
    setSelectedFormat('');
    setModifierLevels({});
    setSelectedAudiences([]);
    setMessageAnalyses({});
    setCurrentConversationId(null);
  };

  const loadConversation = (conversation) => {
    setMessages([
      {
        id: Date.now(),
        type: 'input',
        text: conversation.input,
        timestamp: conversation.timestamp,
        voice: conversation.message.voice,
        role: conversation.message.role,
        format: conversation.message.format,
        modifiers: conversation.message.modifiers,
        audience: conversation.message.audience
      },
      {
        id: Date.now() + 1,
        type: 'output',
        text: conversation.output,
        timestamp: conversation.timestamp,
        voice: conversation.message.voice,
        role: conversation.message.role,
        format: conversation.message.format,
        modifiers: conversation.message.modifiers,
        audience: conversation.message.audience
      }
    ]);
    setSelectedVoice(conversation.message.voice);
    setSelectedRole(conversation.message.role);
    setSelectedFormat(conversation.message.format);
    setModifierLevels(conversation.message.modifiers);
    setSelectedAudiences(conversation.message.audience || []);
    setCurrentConversationId(conversation.id);
    setMessageAnalyses({});
  };

  const adjustModifier = (modifierId, delta) => {
    setModifierLevels(prev => {
      const currentLevel = parseInt(prev[modifierId] || 0);
      
      // Simple increment/decrement by delta
      let newLevel = currentLevel + delta;
      
      // Clamp to -3 to +3 range
      newLevel = Math.max(-3, Math.min(3, newLevel));
      
      const updated = {
        ...prev,
        [modifierId]: newLevel
      };
      
      return updated;
    });
  };

  const removeModifier = (modifier) => {
    setModifierLevels(prev => {
      const updated = { ...prev };
      delete updated[modifier];
      return updated;
    });
  };

  // Touch handlers using consolidated hook - UPDATED: no longer need expandedSection
  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchHandler(
    () => {}
  );

  // Mobile navigation touch handlers
  const { 
    handleTouchStart: handleMobileNavTouchStart, 
    handleTouchMove: handleMobileNavTouchMove, 
    handleTouchEnd: handleMobileNavTouchEnd 
  } = useTouchHandler(() => setShowMobileNav(false));





  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Helper function to get the effective prompt for any parameter
  const getEffectivePrompt = (item) => {
    if (!item) return null;
    // Frontend handles prompt selection logic: updated_prompt if exists, otherwise starter_prompt
    const prompt = item.updated_prompt || item.starter_prompt || null;
    return prompt;
  };



  const buildToneShiftPromptJson = () => {
    
    // Start with the task (same as backend)
    const toneshiftJsonPrompt = {
      "task": "Your task is to rewrite provided text using the following instructions."
    };
    
    // Add the input text (handle parameter-only changes)
    if (inputText.trim()) {
      toneshiftJsonPrompt["original_message"] = inputText.trim();
    } else if (messages.length > 0) {
      // Parameter-only change - get original message from conversation history
      const firstUserMessage = messages.find(msg => msg.type === 'input');
      toneshiftJsonPrompt["original_message"] = firstUserMessage ? firstUserMessage.text : "(No original message found)";
    } else {
      toneshiftJsonPrompt["original_message"] = "(No text entered)";
    }
    
      // Add voice instructions (only if selected)
  if (selectedVoice) {
    const voice = voices.find(v => v.id === selectedVoice);
    if (voice) {
      const voicePrompt = getEffectivePrompt(voice);
      if (voicePrompt) {
        toneshiftJsonPrompt["voice"] = voicePrompt;
      }
    }
  }
    
    // Add role instructions (only if selected)
    if (selectedRole) {
      const role = roles.find(r => r.id === selectedRole);
      if (role) {
        const rolePrompt = getEffectivePrompt(role);
        if (rolePrompt) {
          toneshiftJsonPrompt["role"] = rolePrompt;
        }
      }
    }
    
    // Add format instructions (only if selected)
    if (selectedFormat) {
      const format = formats.find(f => f.id === selectedFormat);
      if (format) {
        const formatPrompt = getEffectivePrompt(format);
        if (formatPrompt) {
          toneshiftJsonPrompt["format"] = formatPrompt;
        }
      }
    }
    
    // Add audience instructions (only if selected)
    if (selectedAudiences?.length > 0) {
      const audiencePrompts = selectedAudiences.map(id => {
        const audience = audiences.find(a => a.id === id);
        if (audience) {
          const prompt = getEffectivePrompt(audience);
          return prompt;
        }
        return null;
      }).filter(Boolean);
      if (audiencePrompts.length > 0) {
        toneshiftJsonPrompt["audience"] = audiencePrompts.join(' AND ');
      }
    }
    
        // Add modifier instructions (only if active)
    
    const activeModifiers = Object.entries(modifierLevels)
      .filter(([_, level]) => parseInt(level) !== 0)
      .map(([modifierId, level]) => {
        const modifier = modifiers.find(m => m.id === modifierId);
        if (!modifier) {
          return null;
        }

        const levelInt = parseInt(level);
        let modifierPrompt = '';
        let levelDisplayName = '';

        // Map numeric level to level name based on modifier type
        const levelName = getModifierLevelName(modifier.name, levelInt);
        
        const levelObj = modifier.levels?.find(l => l.level_name === levelName);

        if (levelObj) {
          // Use the same logic as the backend's get_prompt() method
          modifierPrompt = getEffectivePrompt(levelObj);
          levelDisplayName = getLevelDisplayName(modifier, levelInt);
        }

        const result = modifierPrompt ? `${levelDisplayName}: ${modifierPrompt}` : null;
        return result;
      }).filter(Boolean);

    if (activeModifiers.length > 0) {
      toneshiftJsonPrompt["modifiers"] = activeModifiers.join(' AND ');
    }
    
        // Add model information
    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
    if (selectedModelInfo) {
      toneshiftJsonPrompt["model"] = `${selectedModelInfo.display} (${selectedModelInfo.model})`;
    }

    return toneshiftJsonPrompt;
  };

  const buildToneShiftPromptText = () => {
    const toneshiftJsonPrompt = buildToneShiftPromptJson();
    const parts = [];
    
    // Add task
    parts.push(toneshiftJsonPrompt.task);
    
    // Add voice instruction
    if (toneshiftJsonPrompt.voice) {
      parts.push(`Voice: ${toneshiftJsonPrompt.voice}`);
    }
    
    // Add role instruction
    if (toneshiftJsonPrompt.role) {
      parts.push(`Role: ${toneshiftJsonPrompt.role}`);
    }
    
    // Add format instruction
    if (toneshiftJsonPrompt.format) {
      parts.push(`Format: ${toneshiftJsonPrompt.format}`);
    }
    
    // Add audience instruction
    if (toneshiftJsonPrompt.audience) {
      parts.push(`Audience: ${toneshiftJsonPrompt.audience}`);
    }
    
    // Add modifier instructions
    if (toneshiftJsonPrompt.modifiers) {
      parts.push(`Modifiers: ${toneshiftJsonPrompt.modifiers}`);
    }
    
    // Add conversation history for parameter-only changes
    if (!inputText.trim() && messages.length > 0) {
      // This is a parameter-only change, include conversation history
      const conversationHistory = [];
      
      // Get the first user message (original message)
      const firstUserMessage = messages.find(msg => msg.type === 'input');
      if (firstUserMessage) {
        conversationHistory.push(`User: ${firstUserMessage.text}`);
      }
      
      // Get the last 2 AI responses
      const aiMessages = messages.filter(msg => msg.type === 'output').slice(-2);
      aiMessages.forEach(msg => {
        conversationHistory.push(`Assistant: ${msg.text}`);
      });
      
      if (conversationHistory.length > 0) {
        parts.push(`Conversation history:\n${conversationHistory.join('\n')}`);
      }
    }
    
    // Add the input text
    if (toneshiftJsonPrompt.original_message) {
      parts.push(`original message:\n${toneshiftJsonPrompt.original_message}`);
    }
    
    // Add instruction for parameter-only changes
    if (!inputText.trim() && messages.length > 0) {
      parts.push(`Continue iterating on this message with the updated parameters.`);
    }
    
    // Add model information
    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
    if (selectedModelInfo) {
      parts.push(`Model: ${selectedModelInfo.display} (${selectedModelInfo.model})`);
    }
    
    const finalPrompt = parts.join('\n\n');
    return finalPrompt;
  };

  const handlePromptPreview = () => {
    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
    const { json: toneshiftJsonPrompt, text: formattedPrompt } = buildToneShiftPrompt({
      inputText,
      messages,
      voices, roles, formats, audiences, modifiers,
      selectedVoice, selectedRole, selectedFormat, selectedAudiences,
      modifierLevelsMap: activeModifierLevels(modifierLevels),
      selectedModelInfo,
      getModifierLevelName,
      getLevelDisplayName
    });
    setSelectedPrompt(formattedPrompt);
    setSelectedStructuredJson(toneshiftJsonPrompt);
    setShowPreviewModal(true);
  };

  const showMessageInfo = (message, messageType) => {
    setSelectedMessage(message);
    setSelectedMessageType(messageType);
    setShowInfoModal(true);
  };

  const saveAsContent = async (message) => {
    try {
      // Create a dialog to get title and description
      const title = prompt('Enter a title for this content:', 'Tone-shifted content');
      if (!title) return; // User cancelled
      
      const description = prompt('Enter a description (optional):', 'Generated using ToneShift');
      
      const contentData = {
        title: title,
        description: description,
        content: message.text,
        content_type: 1 // Assuming 1 is the default content type, adjust as needed
      };
      
      await toneShiftApi.createContent(contentData);
      toast.success('Content saved successfully!');
    } catch (error) {
      notifyError('saving content', error, toast);
    }
  };

  const getModifierLevel = (modifierLevels, modifierId) => {
    const level = parseInt(modifierLevels[modifierId] || 0);
    return level;
  };

  const getModifierLabels = (modifier) => {
    // Use the left and right properties from the transformed modifier data
    if (modifier.left && modifier.right) {
      return { left: modifier.left, right: modifier.right };
    }
    
    if (!modifier.levels || modifier.levels.length === 0) {
      return { left: 'Low', right: 'High' };
    }
    
    // Create a mapping of modifier names to their proper labels
    const labelMappings = {
      'length': { left: 'Most Concise', right: 'Most Detailed' },
      'assertiveness': { left: 'Most Collaborative', right: 'Most Decisive' },
      'directness': { left: 'Most Diplomatic', right: 'Most Blunt' },
      'energy': { left: 'Most Calm', right: 'Most Excited' },
      'formality': { left: 'Most Casual', right: 'Most Professional' },
      'urgency': { left: 'Most Relaxed', right: 'Most Immediate' },
      'humor': { left: 'Most Humorous', right: 'Most Serious' }
    };
    
    // If we have a specific mapping for this modifier, use it
    if (labelMappings[modifier.name]) {
      return labelMappings[modifier.name];
    }
    
    // Try case-insensitive matching
    const modifierNameLower = modifier.name.toLowerCase();
    if (labelMappings[modifierNameLower]) {
      return labelMappings[modifierNameLower];
    }
    
    // Otherwise, try to extract from level names
    const sortedLevels = [...modifier.levels].sort((a, b) => {
      // Sort levels using the same logic as toneShiftDataTransformers.js
      const levelOrder = {
        'most_collaborative': 1, 'more_collaborative': 2, 'collaborative': 3,
        'decisive': 4, 'more_decisive': 5, 'most_decisive': 6,
        'most_concise': 1, 'more_concise': 2, 'concise': 3,
        'detailed': 4, 'more_detailed': 5, 'most_detailed': 6,
        'most_professional': 1, 'more_professional': 2, 'professional': 3,
        'personal': 4, 'more_personal': 5, 'most_personal': 6,
        'most_diplomatic': 1, 'more_diplomatic': 2, 'diplomatic': 3,
        'blunt': 4, 'more_blunt': 5, 'most_blunt': 6,
        'most_calm': 1, 'more_calm': 2, 'calm': 3,
        'excited': 4, 'more_excited': 5, 'most_excited': 6,
        'most_casual': 1, 'more_casual': 2, 'casual': 3,
        'professional': 4, 'more_professional': 5, 'most_professional': 6,
        'most_relaxed': 1, 'more_relaxed': 2, 'relaxed': 3,
        'immediate': 4, 'more_immediate': 5, 'most_immediate': 6,
        'most_humorous': 1, 'more_humorous': 2, 'humorous': 3,
        'serious': 4, 'more_serious': 5, 'most_serious': 6
      };
      return (levelOrder[a.level_name] || 0) - (levelOrder[b.level_name] || 0);
    });
    
    if (sortedLevels.length >= 2) {
      const leftLabel = sortedLevels[0].level_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const rightLabel = sortedLevels[sortedLevels.length - 1].level_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return { left: leftLabel, right: rightLabel };
    }
    
    return { left: 'Low', right: 'High' };
  };

  // Use consolidated modifier level mapping
  const getModifierLevelName = (modifierName, level) => {
    return getModifierLevelNameFromMapping(modifierName, level);
  };

  // Use consolidated modifier level display mapping
  const getLevelDisplayName = (modifier, level) => {
    const displayName = getModifierLevelDisplayName(modifier.name, level);
    return displayName;
  };

  // Unified send validation
  const canSend = (opts = { showToast: false }) => {
    const hasText = inputText.trim().length > 0;
    const hasHistory = messages.length > 0;
    const hasParamChanges =
      !!selectedVoice ||
      !!selectedRole ||
      !!selectedFormat ||
      selectedAudiences.length > 0 ||
      Object.values(modifierLevels).some(l => parseInt(l) !== 0);

    const allowed = hasText || (hasHistory && hasParamChanges);
    if (!allowed && opts.showToast) {
      toast.error('Please enter some text to transform or adjust parameters');
    }
    return allowed;
  };

  // Edit modifier functionality
  const handleEditModifier = (modifier) => {
    setEditingModifier(modifier);
    setModifierEditForm({
      name: modifier.name,
      description: modifier.description,
      left: modifier.left,
      right: modifier.right,
      prompts: modifier.prompts || {
        '-3': '',
        '-2': '',
        '-1': '',
        '0': '',
        '1': '',
        '2': '',
        '3': ''
      },
      levelLabels: modifier.level_labels || {
        '-3': '',
        '-2': '',
        '-1': '',
        '0': 'Balanced',
        '1': '',
        '2': '',
        '3': ''
      }
    });
    setShowModifierEditModal(true);
  };

  const handleEditSave = async () => {
    try {
      const data = {
        name: editForm.name,
        description: editForm.description,
        updated_prompt: editForm.prompt  // Send as updated_prompt instead of prompt
      };

      switch (editingItem.type) {
        case 'voice':
          await toneShiftApi.updateVoice(editingItem.id, data);
          break;
        case 'role':
          await toneShiftApi.updateRole(editingItem.id, data);
          break;
        case 'format':
          await toneShiftApi.updateFormat(editingItem.id, data);
          break;
        case 'audience':
          await toneShiftApi.updateAudience(editingItem.id, data);
          break;
        case 'modifier':
          await toneShiftApi.updateModifier(editingItem.id, data);
          break;
        default:
          throw new Error(`Invalid item type: ${editingItem.type}`);
      }
      
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      setEditForm({ name: '', description: '', prompt: '' });
      fetchToneShiftItems(); // Refresh the lists
    } catch (error) {
      notifyError('updating item', error, toast);
    }
  };

  // Add new item functionality
  const handleAddNew = (type) => {
    if (type === 'modifier') {
      setAddModifierForm({
        name: '',
        description: '',
        left: '',
        right: '',
        prompts: {
          '-3': '',
          '-2': '',
          '-1': '',
          '0': '',
          '1': '',
          '2': '',
          '3': ''
        },
        levelLabels: {
          '-3': '',
          '-2': '',
          '-1': '',
          '0': 'Balanced',
          '1': '',
          '2': '',
          '3': ''
        }
      });
      setShowAddModifierModal(true);
    } else {
      setAddForm({
        name: '',
        description: '',
        prompt: '',
        type: type
      });
      setShowAddModal(true);
    }
  };

  const handleAddSave = async () => {
    try {
      const data = {
        name: addForm.name,
        description: addForm.description,
        updated_prompt: addForm.prompt  // Send as updated_prompt instead of prompt
      };

      switch (addForm.type) {
        case 'voice':
          await toneShiftApi.createVoice(data);
          break;
        case 'role':
          await toneShiftApi.createRole(data);
          break;
        case 'format':
          await toneShiftApi.createFormat(data);
          break;
        case 'audience':
          await toneShiftApi.createAudience(data);
          break;
        default:
          throw new Error(`Invalid item type: ${addForm.type}`);
      }
      
      toast.success('Item created successfully');
      setShowAddModal(false);
      setAddForm({ name: '', description: '', prompt: '', type: '' });
      fetchToneShiftItems(); // Refresh the lists
    } catch (error) {
      notifyError('creating item', error, toast);
    }
  };

  // Analysis prompt functionality (simplified for now)
  const handleAnalysisPromptEdit = async () => {
    try {
      // For now, just update the local state
      // In a future update, this could be saved to user preferences
      toast.success('Analysis prompt updated successfully');
      setShowAnalysisEditModal(false);
    } catch (error) {
      notifyError('saving analysis prompt', error, toast);
    }
  };







  const handleEditItem = (item) => {
    
    // Set up the editing item with proper prompt handling
    let editingItem = {
      ...item,
      // Store the original prompt for reference
      originalPrompt: item.starter_prompt || item.prompt || ''
    };

    // Special handling for modifiers - ensure levels are properly set up
    if (item.type === 'modifier' && item.levels) {
      editingItem = {
        ...editingItem,
        // For modifiers, we don't use the main prompt field
        levels: item.levels.map(level => ({
          ...level,
          // Ensure each level has the proper prompt structure
          updated_prompt: level.updated_prompt || null,
          starter_prompt: level.starter_prompt || level.prompt || ''
        }))
      };
      
    } else {
      // For non-modifiers, use the regular prompt handling
      editingItem.prompt = item.updated_prompt || item.starter_prompt || item.prompt || '';
    }

    setEditingSlideItem(editingItem);
    setShowEditSlideModal(true);
  };





  const handleEditSlideSave = async () => {
    try {
      
      // Check if user is authenticated
      if (!user) {
        toast.error('Please log in to save changes');
        return;
      }
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        return;
      }
      
      // Handle different item types
      if (editingSlideItem.type === 'modifier') {
        // For modifiers, update each level individually
        const levelUpdates = editingSlideItem.levels.map(level => ({
          id: level.id,
          // Convert empty strings to null when saving, otherwise use the value
          updated_prompt: level.updated_prompt && level.updated_prompt.trim() !== '' ? level.updated_prompt : null
        }));

        // Update the modifier levels
        for (const levelUpdate of levelUpdates) {
          // Always send the update, even if updated_prompt is null (to revert to starter prompt)
          await toneShiftApi.updateModifierLevel(levelUpdate.id, {
            updated_prompt: levelUpdate.updated_prompt
          });


          

        }

        // Update the modifier itself
        await toneShiftApi.updateModifier(editingSlideItem.id, {
          name: editingSlideItem.name,
          description: editingSlideItem.description
        });


        
        // Refresh the items to show the updated state
        await fetchToneShiftItems();
      } else {
        // For non-modifiers, use the direct parameter APIs
        // Only send the updated_prompt field to avoid triggering name validation
        // Convert empty strings to null when saving
        const updateData = {
          updated_prompt: editingSlideItem.prompt && editingSlideItem.prompt.trim() !== '' ? editingSlideItem.prompt : null
        };

        switch (editingSlideItem.type) {
          case 'voice':
            await toneShiftApi.updateVoice(editingSlideItem.id, updateData);
            break;
          case 'role':
            await toneShiftApi.updateRole(editingSlideItem.id, updateData);
            break;
          case 'format':
            await toneShiftApi.updateFormat(editingSlideItem.id, updateData);
            break;
          case 'audience':
            await toneShiftApi.updateAudience(editingSlideItem.id, updateData);
            break;
          default:
            throw new Error(`Invalid item type: ${editingSlideItem.type}`);
        }

        // Refresh the items to show the updated state
        await fetchToneShiftItems();
      }
      
              // Show success message for non-204 responses
        if (editingSlideItem.type !== 'modifier') {
          toast.success('Item updated successfully');
        }
      setShowEditSlideModal(false);
      setEditingSlideItem(null);
      // fetchToneShiftItems() is already called in the 204 handling above
    } catch (error) {
      notifyError('updating item', error, toast);
    }
  };



  // Compute chip items
  const chipItems = [
    selectedVoice && {
      key: `voice-${selectedVoice}`,
      label: voices.find(v => v.id === selectedVoice)?.name || 'Voice',
      onRemove: () => setSelectedVoice(''),
    },
    selectedRole && {
      key: `role-${selectedRole}`,
      label: roles.find(r => r.id === selectedRole)?.name || 'Role',
      onRemove: () => setSelectedRole(''),
    },
    selectedFormat && {
      key: `format-${selectedFormat}`,
      label: formats.find(f => f.id === selectedFormat)?.name || 'Format',
      onRemove: () => setSelectedFormat(''),
    },
    ...selectedAudiences.map(id => {
      const a = audiences.find(x => x.id === id);
      return a ? {
        key: `aud-${id}`,
        label: a.name,
        onRemove: () => setSelectedAudiences(prev => prev.filter(x => x !== id)),
      } : null;
    }),
    ...Object.entries(modifierLevels).map(([id, level]) => {
      if (parseInt(level) === 0) return null;
      const mod = modifiers.find(m => m.id === id);
      if (!mod) return null;
      return {
        key: `mod-${id}`,
        label: `${mod.name}: ${getLevelDisplayName(mod, parseInt(level))}`,
        onRemove: () => adjustModifier(id, -parseInt(level)),
      };
    })
  ].filter(Boolean);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="tone-shift-page">
        <div className="loading-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }



  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className="tone-shift-page">
        <div className="auth-required">
          <h2>Authentication Required</h2>
          <p>Please log in to access tone shift features.</p>
          <button onClick={() => navigate('/login')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tone-shift-page">
      {/* Header */}
      <div className={`tone-shift-header ${isNavCollapsed ? 'nav-collapsed' : ''}`} ref={headerRef}>
        <div className="header-content">
          <div className="header-main">
            {/* Mobile hamburger menu */}
            <button
              className="mobile-hamburger-btn"
              onClick={() => setShowMobileNav(true)}
              aria-label="Open navigation menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>

            <h1>
              <img src="/images/toneshift.png" alt="ToneShift" className="header-title-icon" />
              ToneShift
            </h1>
            <p>Transform your writing with voice, modifiers, and audience targeting</p>
          </div>
          <div className="header-actions">
            <button
              onClick={startNewConversation}
              className="header-btn"
              title="Start new conversation"
            >
              <Plus className="header-icon" />
            </button>
            <button
              onClick={() => setShowHistoryModal(true)}
              className="header-btn"
              title="View conversation history"
            >
              <Clock className="header-icon" />
            </button>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="chat-container">
        {/* Messages Area */}
        <div className="messages-area" ref={messagesAreaRef}>
          {messages.length === 0 ? (
            <div className="empty-chat">
              <Wand2 className="empty-icon" />
              <h3>Start a conversation</h3>
              <p>Type your message below and use the toolbox to transform it</p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message) => (
                <div key={message.id} className={`message ${message.type}`}>
                  <div className="message-content">
                    {message.type === 'output' && message.additionalContext ? (
                      <>
                        <div className="message-text">
                          {message.additionalContext}
                        </div>
                        <div className="response-box">
                          <div className="response-box-header">
                            <button
                              onClick={() => copyToClipboard(message.text)}
                              className="response-copy-btn"
                              title="Copy response"
                            >
                              <Copy className="toolbox-icon" />
                            </button>
                          </div>
                          <div className="response-box-content">
                            {message.text}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="message-text">
                        {message.text}
                      </div>
                    )}
                    <div className="message-timestamp">{message.timestamp}</div>
                  </div>
                  
                  {/* Message Toolbox */}
                  <div className="message-toolbox">
                    {/* Information button */}
                      <button
                      onClick={() => showMessageInfo(message, message.type === 'input' ? 'user_input' : 'llm_response')}
                      className="toolbox-btn"
                      title="View message information"
                    >
                      <Info className="toolbox-icon" />
                      </button>
                    
                    {message.analysis ? (
                      <button
                        onClick={() => handleMessageAnalyze(message.id)}
                        className="toolbox-btn analysis-status-btn"
                        title="View analysis"
                      >
                        <BarChart3 className="toolbox-icon" />
                        <span className="analysis-status-text">Analyzed</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleMessageAnalyze(message.id)}
                        className="toolbox-btn"
                        title="Analyze message"
                      >
                        <BarChart3 className="toolbox-icon" />
                      </button>
                    )}
                    {message.type === 'output' && !message.additionalContext && (
                      <>
                        <button
                          onClick={() => copyToClipboard(message.text)}
                          className="toolbox-btn"
                          title="Copy output"
                        >
                          <Copy className="toolbox-icon" />
                        </button>
                        <button
                          onClick={() => saveAsContent(message)}
                          className="toolbox-btn"
                          title="Save as content"
                        >
                          <Save className="toolbox-icon" />
                        </button>
                      </>
                    )}
                    {message.type === 'output' && message.additionalContext && (
                      <button
                        onClick={() => saveAsContent(message)}
                        className="toolbox-btn"
                        title="Save as content"
                      >
                        <Save className="toolbox-icon" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {isGenerating && (
                <div className="message output">
                  <div className="message-content">
                    <div className="generating-indicator">
                      <Sparkles className="generating-icon spinning" />
                      <span>Transforming your text...</span>
                    </div>
                  </div>
                </div>
              )}
              

            </div>
          )}
        </div>

        {/* Input Area */}
        <div className={`input-area ${isNavCollapsed ? 'nav-collapsed' : ''}`} ref={inputRef}>
          {/* Input Toolbox */}
          <div className="input-toolbox">
            <div className="toolbox-controls">
              {/* Main Parameter Controls */}
              <div className="main-toolbox-controls">
              <Selector
                title="Format"
                icon={{ png: '/images/medium.png', gif: '/images/medium.gif' }}
                items={formats}
                selected={selectedFormat}
                onSelect={(id) => setSelectedFormat(id)}
                onEdit={(item) => handleEditItem(item)}
              />

              <Selector
                title="Audience"
                icon={{ png: '/images/audience.png', gif: '/images/audience.gif' }}
                items={audiences}
                selected={selectedAudiences}
                multi
                onSelect={(id, checked) =>
                  setSelectedAudiences(prev => checked ? [...prev, id] : prev.filter(x => x !== id))
                }
                onEdit={(item) => handleEditItem(item)}
              />

              {/* Modifiers Widget - REPLACED with Selector */}
              <Selector
                title="Modifiers"
                type="modifier"
                icon={{ png: '/images/modifiers.png', gif: '/images/modifiers.gif' }}
                items={modifiers}
                selected={modifierLevels}
                multi={false}
                showNone={false}
                onSelect={() => {}} // Not used for modifiers
                onEdit={(item) => handleEditItem(item)}
                // Modifier-specific props
                modifierLevels={modifierLevels}
                onAdjust={adjustModifier}
                getModifierLabels={getModifierLabels}
                getLevelDisplayName={getLevelDisplayName}
              />

              <Selector
                title="Voice"
                icon={{ png: '/images/voice.png', gif: '/images/voice.gif' }}
                items={voices}
                selected={selectedVoice}
                onSelect={(id) => setSelectedVoice(id)}
                onEdit={(item) => handleEditItem(item)}
              />

              <Selector
                title="Role"
                icon={{ png: '/images/role.png', gif: '/images/role.gif' }}
                items={roles}
                selected={selectedRole}
                onSelect={(id) => setSelectedRole(id)}
                onEdit={(item) => handleEditItem(item)}
              />
            </div>

              {/* Subtle Controls - Right Side */}
              <div className="subtle-toolbox-controls">
                {/* Model Selector */}
                <Selector
                  title={(() => {
                    if (modelsLoading) return "Loading...";
                    if (modelsError) return "Error";
                    if (!selectedModel || !availableModels.length) return "Model";
                    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
                    return selectedModelInfo ? selectedModelInfo.display || selectedModelInfo.model : "Model";
                  })()}
                  icon={{ png: '/images/models.png', gif: '/images/models.gif' }}
                  items={(availableModels || []).map(m => ({
                    id: m.id,
                    name: m.display || m.model,
                    description: m.provider ? `${m.provider} • ${m.model}` : m.model,
                  }))}
                  selected={selectedModel}
                  onSelect={setSelectedModel}
                  multi={false}
                  showNone={false}
                  disabled={!!modelsLoading}
                />

                                  {/* Prompt Preview Toggle */}
                  <button
                    onClick={handlePromptPreview}
                    className="subtle-preview-btn"
                    title="View full LLM prompt"
                  >
                    <Eye className="subtle-preview-icon" />
                    <span className="subtle-preview-text">Preview</span>
                  </button>
              </div>
            </div>
          </div>

          {/* Input Container with Confined Selection Chips */}
          <div className="input-container">
            {/* Selection Chips - Confined within input area */}
            <Chips
              items={chipItems}
              onClearAll={() => {
                setSelectedVoice('');
                setSelectedRole('');
                setSelectedFormat('');
                setSelectedAudiences([]);
                setModifierLevels({});
              }}
            />

          {/* Input Box */}
          <div className="input-box">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="input-textarea"
              placeholder={buildPlaceholder({
                voices, roles, formats,
                selectedVoice, selectedRole, selectedFormat, selectedAudiences, modifierLevels
              })}
              disabled={isGenerating}
              rows={4}
            />
            {/* Character counter for mobile */}
            {inputText.length > 0 && (
              <div className="input-character-counter">
                {inputText.length} chars
              </div>
            )}
            
            <button
              onClick={handleSend}
              disabled={!canSend() || isGenerating}
              className="send-btn"
            >
              <img 
                src="/images/generate.png" 
                alt="Generate" 
                className="send-icon"
                onMouseEnter={(e) => e.target.src = "/images/generate.gif"}
                onMouseLeave={(e) => e.target.src = "/images/generate.png"}
              />
            </button>
          </div>
        </div>
      </div>
            </div>

      {/* Edit Modal */}
      <EditModal
        isOpen={showEditSlideModal}
        onClose={() => setShowEditSlideModal(false)}
        editingItem={editingSlideItem}
        onSave={handleEditSlideSave}
        isLoading={false}
      />

      {/* Add Modal */}
      <AddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        type={addForm.type}
        formData={addForm}
        onSave={handleAddSave}
        isLoading={false}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        conversations={conversations}
        currentConversationId={currentConversationId}
        onLoadConversation={loadConversation}
        onStartNewConversation={startNewConversation}
      />

      {/* Message Info Modal */}
      <MessageInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        message={selectedMessage}
        messageType={selectedMessageType}
      />

      {/* Analysis Slide Modal */}
      {showAnalysisSlideModal && selectedAnalysisMessage && (
        <div className="analysis-slide-modal-overlay" onClick={() => setShowAnalysisSlideModal(false)}>
          <div className="analysis-slide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="analysis-slide-modal-header">
              <h3>Text Analysis</h3>
              <div className="analysis-header-actions">
                <button
                  onClick={() => {
                    setShowAnalysisSlideModal(false);
                    setShowAnalysisEditModal(true);
                  }}
                  className="analysis-edit-btn"
                  title="Edit analysis prompt"
                >
                  <Edit3 className="analysis-edit-icon" />
                </button>
                <button onClick={() => setShowAnalysisSlideModal(false)} className="modal-close">
                  <X className="close-icon" />
                </button>
              </div>
            </div>
            <div className="analysis-slide-modal-content">
              <div className="analysis-section">
                <h4>Original Text</h4>
                <div className="original-text">
                  {selectedAnalysisMessage.text}
                </div>
              </div>
              
              {isAnalysisLoading ? (
                <div className="analysis-loading-skeleton">
                  <div className="skeleton-section">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-stats">
                      <div className="skeleton-stat"></div>
                      <div className="skeleton-stat"></div>
                      <div className="skeleton-stat"></div>
                    </div>
                  </div>
                  <div className="skeleton-section">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-tones">
                      <div className="skeleton-tone"></div>
                      <div className="skeleton-tone"></div>
                      <div className="skeleton-tone"></div>
                    </div>
                  </div>
                  <div className="skeleton-section">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-messages">
                      <div className="skeleton-message"></div>
                      <div className="skeleton-message"></div>
                    </div>
                  </div>
                  <div className="skeleton-section">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-prompt"></div>
                  </div>
                </div>
              ) : selectedAnalysisMessage.analysis && (
                <>
                  <div className="analysis-section">
                    <h4>Statistics</h4>
                    <div className="analysis-stats">
                      <div className="stat-item">
                        <span className="stat-label">Words:</span>
                        <span className="stat-value">{selectedAnalysisMessage.analysis.wordCount}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Characters:</span>
                        <span className="stat-value">{selectedAnalysisMessage.analysis.characterCount}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Analyzed:</span>
                        <span className="stat-value">{selectedAnalysisMessage.analysis.timestamp}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="analysis-section">
                    <h4>Detected Tones</h4>
                    <div className="analysis-tones-detailed">
                      {selectedAnalysisMessage.analysis.tones.length > 0 ? (
                        selectedAnalysisMessage.analysis.tones.map(({tone, count}) => (
                          <div key={tone} className="tone-item">
                            <span className="tone-name">{tone}</span>
                            <span className="tone-count">{count} instances</span>
                          </div>
                        ))
                      ) : (
                        <span className="no-tones">No specific tones detected</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="analysis-section">
                    <h4>Key Messages</h4>
                    <div className="analysis-messages">
                      {selectedAnalysisMessage.analysis?.keyMessages?.length ? (
                        selectedAnalysisMessage.analysis.keyMessages.map((m, i) => (
                          <div key={i} className="message-item">{m}</div>
                        ))
                      ) : (
                        <span className="no-messages">No key messages identified</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="analysis-section">
                    <h4>Analysis Prompt Used</h4>
                    <div className="analysis-prompt-display">
                      {selectedAnalysisMessage.analysis.prompt}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analysis Edit Modal */}
      <AnalysisEditModal
        isOpen={showAnalysisEditModal}
        onClose={() => setShowAnalysisEditModal(false)}
        analysisPrompt={analysisPrompt}
        onSave={handleAnalysisPromptEdit}
        isLoading={false}
      />

      {/* Prompt Preview Modal */}
      <PromptPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        selectedPrompt={selectedPrompt}
        selectedStructuredJson={selectedStructuredJson}
      />
      
      



      {/* Mobile Navigation Bottom Sheet */}
      {showMobileNav && (
        <>
          <div 
            className="dropdown-backdrop show" 
            onClick={() => setShowMobileNav(false)}
          />
          <div 
            className="mobile-nav-modal show"
            onTouchStart={handleMobileNavTouchStart}
            onTouchMove={handleMobileNavTouchMove}
            onTouchEnd={handleMobileNavTouchEnd}
          >
            <div className="modal-gripper">
              <div className="gripper-line"></div>
            </div>
            <div className="mobile-nav-header">
              <h3>Navigation</h3>
              <button 
                onClick={() => setShowMobileNav(false)}
                className="mobile-nav-close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="mobile-nav-list">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className="mobile-nav-item"
                  onClick={() => setShowMobileNav(false)}
                >
                  <img 
                    src={item.icon} 
                    alt={`${item.title} Icon`}
                    className="mobile-nav-icon"
                  />
                  <span className="mobile-nav-title">{item.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ToneShiftPage; 